import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { BrandColors } from '../../global/helpers-marketing';
import { insertElements } from '../../global/render-api';
import type { UIShapeLayouts } from './ui-shape.type';
import {
    UIShapeLayoutProps,
    validateShapeColor,
    validateShapeLayout,
} from './ui-shape.helpers';
import styles from './ui-shape.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIShape
 * @element ui-shape
 * @classdesc Represents a class for <code>ui-shape</code> element.
 * Marketing elements to use shapes in offer.
 * @property {UIBrandColor} [color="lilac"] {@attr color} Color of the shape.
 * @property {UIShapeLayout} [layout="circle"] {@attr layout} Layout for ui-shape.
 * @property {boolean} [animated] {@attr animated} Rotating text in shape.
 * Texts should be inside &lt;ul> tag.
 * @slot
 * @example
 * <ui-shape layout="circle" color="lilac">
 *   Hello, Shape!
 * </ui-shape>
 */
@Component({ tag: 'ui-shape', styles })
export class UIShape extends UIElement {
    /**
     * @type {UIShapeLayouts}
     */
    static readonly Layout: UIShapeLayouts = UIShapeLayoutProps;

    @Attribute(Boolean) animated?: boolean;
    @Attribute({
        type: String,
        default: BrandColors.Lilac,
        validate: validateShapeColor,
    })
    color: string = BrandColors.Lilac;
    @Attribute({
        type: String,
        default: UIShapeLayoutProps.Circle,
        validate: validateShapeLayout,
    })
    layout: string = UIShape.Layout.Circle;

    private animatedItems: NodeListOf<HTMLLIElement> | null = null;
    private currentAnimatedIndex: number = 1;

    /**
     * Advances the rotating text to the next item when the current one
     * finishes animating.
     * @param {Event} e
     * @private
     */
    private handleAnimationEnd = (e: Event): void => {
        const items = this.animatedItems;
        if (!items) {
            return;
        }
        const finishedItem = e.currentTarget as HTMLElement;
        finishedItem.classList.remove('-animated');
        finishedItem.classList.add('-visually-hidden');
        const nextItem = items[this.currentAnimatedIndex];
        nextItem.classList.add('-animated');
        nextItem.classList.remove('-visually-hidden');
        if (++this.currentAnimatedIndex > items.length - 1) {
            this.currentAnimatedIndex = 0;
        }
    };

    /** @private */
    private subscribeEvents(): void {
        if (!this.animated) {
            return;
        }
        const items = this.querySelectorAll<HTMLLIElement>('li');
        if (items.length === 0) {
            return;
        }
        this.animatedItems = items;
        this.currentAnimatedIndex = 1;
        items.forEach((item, idx) => {
            item.classList.toggle('-animated', idx === 0);
            item.classList.toggle('-visually-hidden', idx !== 0);
            item.addEventListener('animationend', this.handleAnimationEnd);
        });
    }

    /** @private */
    private unsubscribeEvents(): void {
        this.animatedItems?.forEach((item) => {
            item.removeEventListener('animationend', this.handleAnimationEnd);
        });
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        insertElements(this, [
            {
                tagName: 'div',
                attributes: {
                    'class': 'ui-shape__content',
                    'aria-live': this.animated ? 'off' : null,
                },
                children: this.childNodes,
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (this.animated) {
            this.setAttribute('aria-live', 'polite');
        }
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
